import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getCollection } from "@/lib/api";
import { useSEO, breadcrumbSchema } from "@/lib/seo";
import PageHeader from "@/components/PageHeader";
import ProductCard from "@/components/shop/ProductCard";
import { StaggerGroup, StaggerItem } from "@/components/motion/Reveal";
import NotFound from "@/pages/NotFound";

export default function Category() {
  const { slug } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["collection", slug],
    queryFn: () => getCollection(slug),
    retry: false,
  });

  const collection = data?.collection;
  const products = data?.products || [];

  useSEO({
    title: collection ? `${collection.name} | Renova Peptide Co.` : "Collection | Renova Peptide Co.",
    description: collection
      ? `${collection.description} Browse ${collection.name} research materials from Renova Peptide Co.`
      : "Research collection at Renova Peptide Co.",
    path: `/category/${slug}`,
    jsonLd: collection
      ? breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Categories", path: "/categories" },
          { name: collection.name, path: `/category/${slug}` },
        ])
      : null,
  });

  if (isError) return <NotFound />;

  return (
    <>
      <PageHeader
        overline="Research Collection"
        title={collection?.name || "Collection"}
        intro={collection?.description}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Categories", path: "/categories" },
          { name: collection?.name || "Collection", path: `/category/${slug}` },
        ]}
      />
      <section className="container-rnv py-16 md:py-20">
        <div className="flex items-center justify-between mb-10">
          <p className="text-sm text-[#85827D]" data-testid="category-count">
            {isLoading ? "Loading…" : `${products.length} products`}
          </p>
          <Link to="/shop" className="text-sm text-[#1E2A44] link-underline font-medium">View full catalogue</Link>
        </div>
        <StaggerGroup className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((p) => (
            <StaggerItem key={p.slug}><ProductCard product={p} /></StaggerItem>
          ))}
        </StaggerGroup>
      </section>
    </>
  );
}
